// Format helpers compartilhados — extraidos do CostTracker para uso na Tela Atividade
// e em qualquer outro componente que precise formatar custo, tokens, duracao e provider.
//
// CANONICO pt-BR (W0.2): todo numero/moeda/data exibido no app DEVE passar por aqui.
// Moeda = USD exibido em pt-BR ("US$ 3,00"); numeros com virgula decimal e ponto de milhar.
//
// Contratos:
//   formatCost     — retorna null para 0/null (caller decide se esconde pill)
//   formatTokens   — retorna null para 0/null
//   formatDuration — retorna '—' para null/undefined; humanizado caso contrario
//   detectProvider — sempre retorna { name, color } (fallback "Other")
//   fmtMoneyUSD    — '—' para null/undefined; "US$ 3,00" caso contrario
//   fmtNumber      — '—' para null/undefined; pt-BR caso contrario
//   fmtDateTime    — '—' para null/invalido; "dd/mm/aaaa, hh:mm" caso contrario
//   fmtDayTime     — '—' para null/invalido; "dd/mm, hh:mm" caso contrario
//   fmtDuration    — alias canonico de formatDuration

// Formatters Intl reutilizados (instanciar Intl.NumberFormat e caro; cache por opcoes).
const moneyUSD = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' });
const moneyFmtCache = new Map<string, Intl.NumberFormat>();
const numberFmtCache = new Map<string, Intl.NumberFormat>();
const dateTimeFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dayTimeFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Moeda USD exibida em pt-BR. Ex: fmtMoneyUSD(3) → "US$ 3,00".
 * `fractionDigits` opcional para custos pequenos (ex: 4 → "US$ 0,0042").
 */
export function fmtMoneyUSD(v: number | null | undefined, fractionDigits?: number): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (fractionDigits === undefined) return moneyUSD.format(v);
  const key = `usd:${fractionDigits}`;
  let fmt = moneyFmtCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    moneyFmtCache.set(key, fmt);
  }
  return fmt.format(v);
}

/**
 * Numero em pt-BR (virgula decimal, ponto de milhar). Ex: fmtNumber(1234.5, 1) → "1.234,5".
 * `fractionDigits` fixa min/max casas decimais quando informado.
 */
export function fmtNumber(v: number | null | undefined, fractionDigits?: number): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const key = `n:${fractionDigits ?? 'auto'}`;
  let fmt = numberFmtCache.get(key);
  if (!fmt) {
    fmt =
      fractionDigits === undefined
        ? new Intl.NumberFormat('pt-BR')
        : new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          });
    numberFmtCache.set(key, fmt);
  }
  return fmt.format(v);
}

/** Data+hora completa pt-BR no fuso local. Ex: "25/06/2026, 14:30". */
export function fmtDateTime(d: string | number | Date | null | undefined): string {
  if (d === null || d === undefined || d === '') return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFmt.format(date);
}

/** Dia+hora compacto pt-BR no fuso local. Ex: "25/06, 14:30". */
export function fmtDayTime(d: string | number | Date | null | undefined): string {
  if (d === null || d === undefined || d === '') return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return dayTimeFmt.format(date);
}

export interface ProviderInfo {
  name: string;
  color: string;
}

export function detectProvider(model: string | null | undefined): ProviderInfo {
  if (!model) return { name: 'Other', color: 'text-raios-muted' };
  const lower = model.toLowerCase();
  if (lower.includes('claude') || lower.includes('haiku') || lower.includes('sonnet') || lower.includes('opus')) {
    return { name: 'Anthropic', color: 'text-orange-400' };
  }
  if (lower.includes('gpt') || lower.includes('openai')) {
    return { name: 'OpenAI', color: 'text-emerald-400' };
  }
  if (lower.includes('gemini')) {
    return { name: 'Google', color: 'text-blue-400' };
  }
  if (lower.includes('llama')) {
    return { name: 'Meta', color: 'text-sky-400' };
  }
  if (lower.includes('mixtral') || lower.includes('mistral')) {
    return { name: 'Mistral', color: 'text-amber-400' };
  }
  if (lower.includes('command')) {
    return { name: 'Cohere', color: 'text-purple-400' };
  }
  return { name: 'Other', color: 'text-raios-muted' };
}

export function formatTokens(n: number | null | undefined): string | null {
  if (n === null || n === undefined || n <= 0) return null;
  if (n >= 1_000_000) return `${fmtNumber(n / 1_000_000, 1)}M`;
  if (n >= 1_000) return `${fmtNumber(n / 1_000, 0)}K`;
  return String(n);
}

/** MTR-03 (admin 2026-06-12): custo grande COMPRIME como tokens —
 *  "US$ 1,2K" / "US$ 3,4M" (antes "US$ 1.234" estourava as caixinhas). */
function compactUSD(n: number): string {
  if (n >= 1_000_000) return `US$ ${fmtNumber(n / 1_000_000, 1)}M`;
  return `US$ ${fmtNumber(n / 1_000, 1)}K`;
}

export function formatCost(n: number | null | undefined): string | null {
  if (n === null || n === undefined || n <= 0) return null;
  if (n < 0.01) return `< ${moneyUSD.format(0.01)}`;
  if (n >= 1000) return compactUSD(n);
  return moneyUSD.format(n);
}

// Variante para totais/headers onde "US$ 0,00" eh um placeholder valido (ex: CostTracker).
export function formatCostStrict(n: number | null | undefined): string {
  if (n === null || n === undefined) return moneyUSD.format(0);
  if (n > 0 && n < 0.01) return `< ${moneyUSD.format(0.01)}`;
  if (n >= 1000) return compactUSD(n);
  return moneyUSD.format(n);
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${fmtNumber(seconds, 1)}s`;
  const totalMinutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds - totalMinutes * 60);
  if (totalMinutes < 60) {
    return remSeconds > 0 ? `${totalMinutes}m ${remSeconds}s` : `${totalMinutes}m`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const remMinutes = totalMinutes - hours * 60;
  return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

/** Alias canonico pt-BR de formatDuration (W0.2). */
export const fmtDuration = formatDuration;
